import React, { useEffect, useState } from "react";
import { Select, Tag } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import "./Contracts.css";
import AddContract from "./AddContract";
import GeneralTable from "../../../commons/GeneralTable";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import UpdateContract from "./UpdateContract";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import { Success } from "../../../commons/Messages/Messages";
import DropdownComp from "../../../commons/Dropdowns/DropdownComp";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import ContractTemplates from "./ContractTemplates";
import PageToolbar from "../../../design-system/PageToolbar";
import {
  CONTRACT_TYPES,
  STATUS_COLORS,
  STATUS_LABELS,
  canContracts,
  getContractType
} from "./contractTypes";

import { useTranslation } from "react-i18next";
const Contracts = () => {
  const { t } = useTranslation("dashboard");
  const [contracts, setContracts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [contractType, setContractType] = useState();
  const [status, setStatus] = useState();
  const [pageSize] = useState(20);

  const getContracts = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/contracts/get`,
        {
          pageSize,
          pageNo: pageNo - 1,
          searchText,
          contractType,
          status
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      );
      if (res.status === 200 || res.status === 304) {
        setContracts(res.data.contracts);
        setTotalCount(res.data.totalCount);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const deleteContract = async id => {
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/contracts/delete/${id}`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      );
      if (res.status === 200 || res.status === 304) {
        Success(t(res.data.successMessage));
        getContracts();
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  useEffect(() => {
    getContracts();
  }, [pageNo, searchText, contractType, status]);

  const columns = [
    {
      title: t("Title"),
      dataIndex: "title",
      key: "title",
      mobile: "title",
      render: (title, obj) => (
        <>
          <div>{title || t("Contract")}</div>
          <Tag color="geekblue">{t(getContractType(obj.contractType).label)}</Tag>
        </>
      )
    },
    {
      title: t("Contract partner"),
      key: "party",
      render: (_, obj) =>
        obj.employee
          ? `${obj.employee.firstName} ${obj.employee.lastName}`
          : [obj.partyName, obj.partyCompany].filter(Boolean).join(", ") || "-"
    },
    {
      title: t("Start Date"),
      dataIndex: "startDate",
      key: "startDate",
      render: date => (date ? dayjs(date).format("DD.MM.YYYY") : "-")
    },
    {
      title: t("End Date"),
      dataIndex: "endDate",
      key: "endDate",
      render: date => (date ? dayjs(date).format("DD.MM.YYYY") : t("open-ended"))
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      mobile: "subtitle",
      render: value => (
        <Tag color={STATUS_COLORS[value || "draft"]}>{t(STATUS_LABELS[value || "draft"])}</Tag>
      )
    },
    {
      title: t("Action"),
      key: "action",
      mobile: "actions",
      render: (text, obj) => {
        const menuItems = [
          {
            key: "view",
            link: `/user/contract/${obj?._id}`
          },
          canContracts("Update") &&
            obj.status !== "signed" && {
              key: "edit",
              component: () => <UpdateContract contract={obj} updateParent={getContracts} />
            },
          canContracts("Delete") && {
            key: "delete",
            component: () => (
              <DeleteModal text="Contract" onClick={() => deleteContract(obj?._id)} />
            )
          }
        ].filter(Boolean);
        return (
          <div>
            <DropdownComp menuItems={menuItems} />
          </div>
        );
      }
    }
  ];

  const handleSearch = val => {
    setPageNo(1);
    setSearchText(val);
  };

  const filters = (
    <>
      <Select
        allowClear
        placeholder={t("Contract type")}
        value={contractType}
        onChange={value => {
          setPageNo(1);
          setContractType(value);
        }}
        options={CONTRACT_TYPES.map(type => ({ value: type.key, label: t(type.label) }))}
      />
      <Select
        allowClear
        placeholder={t("Status")}
        value={status}
        onChange={value => {
          setPageNo(1);
          setStatus(value);
        }}
        options={Object.keys(STATUS_LABELS).map(key => ({
          value: key,
          label: t(STATUS_LABELS[key])
        }))}
      />
    </>
  );
  const actions = (
    <>
      {canContracts("Update") && <ContractTemplates />}
      {canContracts("Create") && <AddContract updateParent={getContracts} />}
    </>
  );

  return (
    <div className="contracts-page">
      <PageToolbar
        filters={
          <>
            <SearchBarComp handleSearch={handleSearch} />
            {filters}
          </>
        }
        actions={actions}
      />
      <GeneralTable
        pageNo={pageNo}
        pageSize={pageSize}
        totalCount={totalCount}
        data={contracts}
        columns={columns}
        setPageNo={setPageNo}
      />
    </div>
  );
};

export default Contracts;
